/**
 * Timeline Track Component (canvas-v2)
 *
 * Single track row displaying video or audio clips.
 * Video mode: clips laid out in sequence.
 * Audio mode: N tracks stacked vertically (60px each) grouped by trackIndex,
 * clips positioned absolutely by startOffset.
 *
 * Memoized for performance with large numbers of clips.
 */

import { memo, useMemo } from 'react'
import type { TimelineClip, TimelineAudioClip } from './types'
import { TimelineClipComponent } from './TimelineClip'
import { TimelineAudioClipComponent } from './TimelineAudioClip'
import { getClipPosition } from './timelineUtils'

interface TimelineTrackProps {
  /** Track type */
  type: 'video' | 'audio'

  /** Track label */
  label: string

  /** Clips on this track */
  clips: TimelineClip[] | TimelineAudioClip[]

  /** Zoom level (pixels per second) */
  zoomLevel: number

  /** Total timeline duration (for track width) */
  totalDuration: number

  /** Selected clip ID */
  selectedClipId: string | null

  /** Callback when clip is selected */
  onSelectClip: (clipId: string) => void

  /** Callback when clip is reordered (video clips only) */
  onReorderClip?: (clipId: string, newOrder: number) => void

  /** Callback when clip is trimmed */
  onTrimClip: (clipId: string, trimStart: number, trimEnd: number) => void

  /** Callback for live trim preview (no history push) */
  onTrimClipPreview?: (clipId: string, trimStart: number, trimEnd: number) => void

  /** Callback when audio startOffset changes */
  onAudioOffsetChange?: (clipId: string, startOffset: number) => void

  /** Callback when audio track index changes */
  onAudioTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Callback when snap position changes during drag */
  onSnapPositionChange?: (position: number | null) => void

  /** Track height in pixels (for video track) */
  height?: number

  /** Number of audio tracks (for stacked layout) */
  audioTrackCount?: number

  /** Whether track is locked */
  locked?: boolean

  /** Minimum width in pixels */
  minWidth?: number
}

const AUDIO_TRACK_HEIGHT = 60

export const TimelineTrack = memo(function TimelineTrack({
  type,
  label,
  clips,
  zoomLevel,
  totalDuration,
  selectedClipId,
  onSelectClip,
  onReorderClip,
  onTrimClip,
  onTrimClipPreview,
  onAudioOffsetChange,
  onAudioTrackIndexChange,
  onSnapPositionChange,
  height = 80,
  audioTrackCount = 1,
  locked = false,
  minWidth = 1000,
}: TimelineTrackProps) {
  const trackWidth = Math.max(totalDuration * zoomLevel, minWidth)

  const videoClips = type === 'video' ? (clips as TimelineClip[]) : []

  // Group audio clips by trackIndex
  const audioClipsByTrack = useMemo(() => {
    if (type !== 'audio') return []
    const audioClips = clips as TimelineAudioClip[]
    const groups: TimelineAudioClip[][] = Array.from({ length: audioTrackCount }, () => [])
    audioClips.forEach((clip) => {
      const idx = Math.min(clip.trackIndex, audioTrackCount - 1)
      groups[idx].push(clip)
    })
    return groups
  }, [type, clips, audioTrackCount])

  const totalAudioHeight = type === 'audio' ? AUDIO_TRACK_HEIGHT * audioTrackCount : height

  return (
    <div className="border-b border-border">
      {/* Track content area */}
      <div
        className="relative bg-canvas"
        style={{
          width: `${trackWidth}px`,
          height: `${type === 'audio' ? totalAudioHeight : height}px`,
        }}
      >
        {/* Track label overlay */}
        <div className="absolute top-1 left-1 z-10 inline-flex items-center gap-1 px-2 py-0.5 bg-card/80 backdrop-blur-sm rounded text-text-secondary text-xs pointer-events-none">
          {label}
        </div>

        {/* Video clips */}
        {type === 'video' && onReorderClip && videoClips.map((clip) => {
          const position = getClipPosition(clip, videoClips, zoomLevel)

          return (
            <div
              key={clip.id}
              className="absolute top-2 bottom-2"
              style={{ left: `${position}px` }}
            >
              <TimelineClipComponent
                clip={clip}
                allClips={videoClips}
                zoomLevel={zoomLevel}
                isSelected={selectedClipId === clip.id}
                trackHeight={height - 16}
                onSelect={onSelectClip}
                onReorder={onReorderClip}
                onTrim={onTrimClip}
                onTrimPreview={onTrimClipPreview}
                onSnapPositionChange={onSnapPositionChange}
                locked={locked}
              />
            </div>
          )
        })}

        {/* Audio clips -- stacked vertically by trackIndex */}
        {type === 'audio' && audioClipsByTrack.map((trackClips, trackIdx) => (
          <div
            key={trackIdx}
            className="absolute left-0 right-0"
            style={{
              top: `${trackIdx * AUDIO_TRACK_HEIGHT}px`,
              height: `${AUDIO_TRACK_HEIGHT}px`,
            }}
          >
            {/* Track separator line (except first) */}
            {trackIdx > 0 && (
              <div className="absolute top-0 left-0 right-0 h-px bg-border/50" />
            )}

            {/* Sub-track label */}
            {audioTrackCount > 1 && (
              <div className="absolute top-1 left-1 z-10 text-text-muted text-xxs pointer-events-none">
                Track {trackIdx + 1}
              </div>
            )}

            {/* Audio clips on this sub-track */}
            <div className="relative h-full">
              {trackClips.map((clip) => (
                <div
                  key={clip.id}
                  className="absolute top-2 bottom-2"
                  style={{ left: '0px' }}
                >
                  <TimelineAudioClipComponent
                    clip={clip}
                    zoomLevel={zoomLevel}
                    isSelected={selectedClipId === clip.id}
                    onSelect={onSelectClip}
                    onTrim={onTrimClip}
                    onOffsetChange={onAudioOffsetChange || (() => {})}
                    onTrackIndexChange={onAudioTrackIndexChange}
                    audioTrackCount={audioTrackCount}
                    trackHeight={AUDIO_TRACK_HEIGHT}
                    locked={locked}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Empty state */}
        {clips.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-foreground/30 text-sm">
              {type === 'video' ? 'No video clips' : 'No audio clips'}
            </div>
          </div>
        )}
      </div>
    </div>
  )
})
